import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';

const ADMIN_COOKIE_KEY = 'zerde_admin_token';
const API_URL = 'https://guide.zerdegroup.kz/api/';

interface UserFormData {
  login: string;
  password: string;
  role: string;
}

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const isEditMode = Boolean(userId);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<UserFormData>({
    login: '',
    password: '',
    role: 'editor',
  });

  useEffect(() => {
    if (isEditMode && userId) {
      const fetchUser = async () => {
        try {
          setIsLoading(true);
          setError('');
          
          const token = Cookies.get(ADMIN_COOKIE_KEY);
          
          if (!token) {
            navigate('/auth');
            return;
          }

          const response = await fetch(`${API_URL}/users/${userId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Ошибка при загрузке пользователя');
          }

          setFormData({
            login: data.login,
            password: '', // пароль не приходит с сервера
            role: data.role
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке данных пользователя');
        } finally {
          setIsLoading(false);
        }
      };

      fetchUser();
    }
  }, [isEditMode, userId, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError('');

      const token = Cookies.get(ADMIN_COOKIE_KEY);
      if (!token) {
        navigate('/auth');
        return;
      }

      const url = isEditMode 
        ? `${API_URL}/users/${userId}` 
        : `${API_URL}/users`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      // Если редактируем и пароль пустой, не отправляем его
      const dataToSend = isEditMode && !formData.password 
        ? { login: formData.login, role: formData.role }
        : formData;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Ошибка при ${isEditMode ? 'обновлении' : 'создании'} пользователя`);
      }

      console.log(`Пользователь успешно ${isEditMode ? 'обновлен' : 'создан'}:`, data);
      // Успешное создание/обновление
      navigate('/admin/panel/users/list');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/panel/users/list');
  };

  if (isLoading && isEditMode && !formData.login) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
        {isEditMode ? 'Редактировать пользователя' : 'Создать пользователя'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ maxWidth: 600 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Логин"
              value={formData.login}
              onChange={(e) => setFormData({ ...formData, login: e.target.value })}
              fullWidth
              required
              disabled={isLoading}
            />

            <TextField
              label={isEditMode ? 'Новый пароль' : 'Пароль'}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required={!isEditMode}
              disabled={isLoading}
              helperText={isEditMode ? 'Оставьте пустым, чтобы не менять пароль' : ''}
            />

            <FormControl fullWidth required disabled={isLoading}>
              <InputLabel>Роль</InputLabel>
              <Select
                value={formData.role}
                label="Роль"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as string })}
              >
                <MenuItem value="editor">Редактор</MenuItem>
                <MenuItem value="admin">Администратор</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !formData.login || (!isEditMode && !formData.password) || !formData.role}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  isEditMode ? 'Сохранить' : 'Создать'
                )}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default UserForm;